import { ArrowUpRight, Building2, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import {
  BECOME_PARTNER_BODY,
  BECOME_PARTNER_HEADING,
  LEARNING_CENTER_LEDE,
  LEARNING_CENTER_PARTNERS,
  LEARNING_CENTER_STATS,
  PARTNER_BENEFITS,
} from '@/data/partners';

/**
 * LearningCenterPartners (/partners — section 2) — "Closer to students,
 * across the world."
 *
 * On the alternating muted band: LEARNING_CENTER_LEDE frames the programme,
 * a small proof strip surfaces LEARNING_CENTER_STATS (learning centres ·
 * countries) as big gold figures, then the ten LEARNING_CENTER_PARTNERS
 * render as a ScrollReveal-staggered grid of hover-lift card links
 * (Building2 chip + name + MapPin country row + "View profile" affordance)
 * raised on bg-background — each links through to its dedicated profile at
 * /partners/learning-centers/:slug. A closing CTA pairs
 * BECOME_PARTNER_HEADING/BODY and a navy enquiry button against the three
 * PARTNER_BENEFITS as icon-chip rows.
 *
 * Motion: SplitReveal headline, ScrollReveal child-staggers throughout.
 * Reduced-motion safe; every institutional fact comes from '@/data/partners',
 * nothing is invented.
 */
export function LearningCenterPartners() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Learning Center Partners
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Closer to students, across the world.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {LEARNING_CENTER_LEDE}
            </p>
          </ScrollReveal>
        </header>

        {/* ── Proof strip ─────────────────────────────────────────────── */}
        <ScrollReveal stagger={0.08} className="mt-10 flex flex-wrap gap-x-14 gap-y-6">
          <div>
            <p className="font-display text-4xl leading-none font-semibold text-brand-gold md:text-5xl">
              {LEARNING_CENTER_STATS.partners}
            </p>
            <p className="mt-2 font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
              Learning centres
            </p>
          </div>
          <div>
            <p className="font-display text-4xl leading-none font-semibold text-brand-gold md:text-5xl">
              {LEARNING_CENTER_STATS.countries}
            </p>
            <p className="mt-2 font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
              Countries
            </p>
          </div>
        </ScrollReveal>

        {/* ── Partner grid ────────────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.06}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {LEARNING_CENTER_PARTNERS.map((partner) => (
            <Link
              key={partner.slug}
              to={`/partners/learning-centers/${partner.slug}`}
              data-cursor
              className="group flex h-full flex-col rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                  <Building2 aria-hidden className="size-6" />
                </div>
                <ArrowUpRight
                  aria-hidden
                  className="size-5 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-gold motion-reduce:transition-none"
                />
              </div>
              <h3 className="mt-5 font-display text-lg leading-tight font-semibold text-foreground">
                {partner.name}
              </h3>
              <p className="mt-3 flex items-center gap-2 text-sm leading-relaxed text-muted-foreground">
                <MapPin aria-hidden className="size-4 shrink-0 text-brand-gold" />
                {partner.country}
              </p>
              <span className="mt-auto inline-flex items-center gap-1.5 pt-5 font-label text-xs tracking-[0.16em] text-brand-gold uppercase underline-offset-4 group-hover:underline">
                View profile
              </span>
            </Link>
          ))}
        </ScrollReveal>

        {/* ── Become-a-partner CTA ────────────────────────────────────── */}
        <div className="mt-16 grid grid-cols-1 gap-10 border-t border-border pt-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
          {/* ── Invitation ──────────────────────────────────────────────── */}
          <div>
            <SplitReveal
              as="h3"
              by="lines"
              className="font-display text-2xl leading-tight text-foreground md:text-3xl"
            >
              {BECOME_PARTNER_HEADING}
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {BECOME_PARTNER_BODY}
              </p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.15}>
              <a
                href="#enquire"
                data-cursor
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-navy/90"
              >
                Enquire about partnering
              </a>
            </ScrollReveal>
          </div>

          {/* ── Why partner ─────────────────────────────────────────────── */}
          <ScrollReveal stagger={0.08} className="flex flex-col gap-5">
            {PARTNER_BENEFITS.map((benefit) => (
              <div key={benefit.title} className="flex items-start gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                  <benefit.Icon aria-hidden className="size-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-display text-base leading-tight font-semibold text-foreground">
                    {benefit.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {benefit.detail}
                  </p>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
